import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaTh, FaList } from 'react-icons/fa';
import { getProducts } from '../services/api';
import ProductCard from '../components/ProductCard';
import '../styles/Products.css';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [query, setQuery] = useState('');
  const [maxPrice, setMaxPrice] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getProducts()
      .then((r) => { if (alive) setProducts(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setProducts([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const ceiling = useMemo(() => {
    if (!products.length) return 10000;
    return Math.max(1000, Math.ceil(Math.max.apply(null, products.map((p) => p.price || 0)) / 500) * 500);
  }, [products]);

  const cats = useMemo(() => {
    const seen = [];
    products.forEach((p) => { if (p.category && seen.indexOf(p.category) === -1) seen.push(p.category); });
    return seen;
  }, [products]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cap = maxPrice === null ? ceiling : maxPrice;
    const list = products.filter((p) => {
      if (category !== 'all' && p.category !== category) return false;
      if ((p.price || 0) > cap) return false;
      if (!q) return true;
      return [p.name, p.nameAr, p.description, p.descriptionAr]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    });

    const sorted = list.slice();
    if (sortBy === 'price-low') sorted.sort((a, b) => a.price - b.price);
    else if (sortBy === 'price-high') sorted.sort((a, b) => b.price - a.price);
    else if (sortBy === 'name') sorted.sort((a, b) => (a.nameAr || a.name || '').localeCompare(b.nameAr || b.name || '', 'ar'));
    else sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return sorted;
  }, [products, category, query, sortBy, maxPrice, ceiling]);

  const cap = maxPrice === null ? ceiling : maxPrice;

  return (
    <div className="products-page">
      <header className="page-header">
        <span className="eyebrow">مختارة بعناية</span>
        <h1>منتجاتنا</h1>
        <p>أشياء صغيرة تُسهّل يومكِ، اخترناها واحدة واحدة.</p>
      </header>

      <div className="container">
        <form className="products-search" role="search" onSubmit={(e) => e.preventDefault()}>
          <label htmlFor="products-q" className="sr-only">ابحثي في المنتجات</label>
          <FaSearch aria-hidden="true" />
          <input
            id="products-q"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ابحثي عن منتج..."
          />
          <button type="submit">بحث</button>
        </form>

        {cats.length > 0 && (
          <nav className="chip-row" aria-label="تصفية حسب الفئة">
            <button
              type="button"
              className={'chip' + (category === 'all' ? ' active' : '')}
              onClick={() => setCategory('all')}
              aria-pressed={category === 'all'}
            >
              الكل
            </button>
            {cats.map((c) => (
              <button
                key={c}
                type="button"
                className={'chip' + (category === c ? ' active' : '')}
                onClick={() => setCategory(c)}
                aria-pressed={category === c}
              >
                {c}
              </button>
            ))}
          </nav>
        )}

        <div className="products-toolbar">
          <div className="toolbar-left">
            <span className="results-count" aria-live="polite">
              {loading ? 'جاري التحميل…' : results.length + ' منتج'}
            </span>
          </div>

          <div className="toolbar-right">
            <div className="price-range">
              <label htmlFor="price-cap">
                السعر حتى <strong>{cap} دج</strong>
              </label>
              <input
                id="price-cap"
                type="range"
                min="0"
                max={ceiling}
                step="100"
                value={cap}
                onChange={(e) => setMaxPrice(parseInt(e.target.value, 10))}
              />
            </div>

            <div className="sort-wrap">
              <label htmlFor="sort" className="sr-only">ترتيب</label>
              <select id="sort" className="sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="newest">الأحدث</option>
                <option value="price-low">السعر: من الأقل للأعلى</option>
                <option value="price-high">السعر: من الأعلى للأقل</option>
                <option value="name">الاسم</option>
              </select>
            </div>

            <div className="view-toggle" role="group" aria-label="طريقة العرض">
              <button
                type="button"
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
                aria-pressed={viewMode === 'grid'}
                aria-label="عرض شبكي"
              >
                <FaTh aria-hidden="true" />
              </button>
              <button
                type="button"
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
                aria-pressed={viewMode === 'list'}
                aria-label="عرض قائمة"
              >
                <FaList aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="products-grid grid" aria-hidden="true">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton-card">
                <div className="skeleton-img" />
                <div className="skeleton-line" />
                <div className="skeleton-line short" />
              </div>
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="no-products">
            <span className="empty-mark" aria-hidden="true">🧺</span>
            <p>لا توجد منتجات تطابق اختياركِ.</p>
          </div>
        ) : (
          <div className={'products-grid ' + viewMode}>
            {results.map((p) => <ProductCard key={p._id} product={p} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
